import api from '../../../config/axios'
import type {
  DailyEnvironmentalData,
  CreateDailyEnvironmentalDataRequest,
  PaginatedDailyEnvironmentalDataResponse,
} from '../../../types/api'

export interface DailyEnvironmentalDataFilters {
  entry_date_from?: string; // YYYY-MM-DD, inclusive. Omit for no lower bound.
  entry_date_to?: string; // YYYY-MM-DD, inclusive. Omit for no upper bound.
}

export const DailyEnvironmentalDataService = {
  list: (page = 1, limit = 10, filters?: DailyEnvironmentalDataFilters) =>
    api.get<PaginatedDailyEnvironmentalDataResponse>('/daily-environmental-data', {
      params: {
        page,
        limit,
        ...(filters?.entry_date_from && { entry_date_from: filters.entry_date_from }),
        ...(filters?.entry_date_to && { entry_date_to: filters.entry_date_to }),
      },
    }).then(res => res.data),

  getToday: () =>
    api.get<DailyEnvironmentalData>('/daily-environmental-data/today').then(res => res.data),

  upsert: (data: CreateDailyEnvironmentalDataRequest) =>
    api.post<DailyEnvironmentalData>('/daily-environmental-data', data).then(res => res.data),

  /**
   * Fetch the .xlsx export as a blob (uses auth). Use with triggerExportDownload().
   * Honors the same optional inclusive date range as list() — unpaginated,
   * every matching row is included.
   */
  getExportBlob: (filters?: DailyEnvironmentalDataFilters) =>
    api.get('/daily-environmental-data/export', {
      responseType: 'blob',
      params: {
        ...(filters?.entry_date_from && { entry_date_from: filters.entry_date_from }),
        ...(filters?.entry_date_to && { entry_date_to: filters.entry_date_to }),
      },
    }).then((res) => res.data as Blob),

  /**
   * Build the export filename the same way the backend does: "environmentaldata",
   * plus the date range if one was applied.
   */
  buildExportFilename: (filters?: DailyEnvironmentalDataFilters) => {
    let filename = 'environmentaldata';
    if (filters?.entry_date_from || filters?.entry_date_to) {
      const from = filters.entry_date_from || 'start';
      const to = filters.entry_date_to || 'today';
      filename += `_${from}_to_${to}`;
    }
    return `${filename}.xlsx`;
  },

  /** Trigger browser download of the export blob. */
  triggerExportDownload: (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
}
